import { Link } from "wouter";
import { ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import PageHero from "@/components/PageHero";
import { IMAGES } from "@/lib/constants";
import LazyImage from "@/components/LazyImage";
import RelatedArticles from "@/components/RelatedArticles";

const cases = [
  {
    title: "Volvo - Prototyppartnerskap",
    desc: "Alla Volvos prototyper och kundanpassningar går genom ASPAB. Komplexa, unika projekt som kräver hög precision och snabb leverans. Volvo förlitar sig på vår tekniska expertis och konsultativa approach.",
    href: "/referenser/volvo",
    image: IMAGES.closeup1,
    tags: ["Prototyper", "Fordonsindustri", "Hög precision"],
  },
  {
    title: "Försvarsindustrin - Säkerhetskritiska komponenter",
    desc: "Vi tillverkar komponenter till försvarssektorn med extrema krav på materialprestanda och kvalitet. Vår pågående ISO-certifiering möjliggör leveranser till denna krävande sektor.",
    href: "/referenser/forsvarsindustrin",
    image: IMAGES.workshop2,
    tags: ["Försvar", "Specialmaterial", "Säkerhetskritiskt"],
  },
];

export default function Referenser() {
  return (
    <Layout>
      <SEOHead
        title="Referenser och kundcase - Volvo, försvarsindustrin och fler | ASPAB"
        description="Läs om hur ASPAB levererar laserskärning till Volvo och försvarsindustrin. Prototyper, storserier och säkerhetskritiska komponenter."
        canonical="https://aspab.com/referenser"
        path="/referenser"
        breadcrumbs={[{ name: "Referenser", href: "/referenser" }]}
              ogImage={IMAGES.ogReferenser}
      />

      <PageHero
        title="Referenser"
        subtitle="Vi levererar till ledande företag inom fordonsindustrin, försvarssektorn och verkstadsindustrin. Här är några av våra samarbeten."
        breadcrumbs={[{ name: "Referenser", href: "/referenser" }]}
      />

      <section className="bg-white py-20 md:py-28">
        <div className="container space-y-10">
          {cases.map((c, i) => (
            <AnimatedSection key={c.title} delay={i * 150}>
              <Link href={c.href} className="group block">
                <div className={`grid lg:grid-cols-2 gap-0 bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-[#2563EB]/20 transition-colors ${i % 2 === 1 ? "lg:direction-rtl" : ""}`}>
                  <div className="aspect-[16/10] lg:aspect-auto overflow-hidden">
                    <LazyImage
                      src={c.image}
                      alt={c.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      wrapperClassName="w-full h-full"
                    />
                  </div>
                  <div className={`p-8 md:p-10 flex flex-col justify-center ${i % 2 === 1 ? "lg:order-first" : ""}`}>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {c.tags.map((tag) => (
                        <span key={tag} className="px-3 py-1 bg-[#2563EB]/10 text-[#2563EB] text-xs font-medium rounded-lg">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 group-hover:text-[#2563EB] transition-colors">
                      {c.title}
                    </h2>
                    <p className="text-gray-500 mt-3 leading-relaxed">{c.desc}</p>
                    <span className="inline-flex items-center gap-2 text-[#2563EB] font-medium mt-5 group-hover:gap-3 transition-all">
                      Läs hela caset <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* Trusted by */}
      <section className="bg-gray-50 py-16 border-y border-gray-200">
        <div className="container text-center">
          <AnimatedSection>
            <p className="text-gray-500 text-sm tracking-wider uppercase mb-8">Branscher vi levererar till</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { name: "Fordonsindustri", desc: "Volvo och underleverantörer" },
                { name: "Försvarssektorn", desc: "Säkerhetskritiska komponenter" },
                { name: "Jordbruk", desc: "Slitstål och reparationsdelar" },
                { name: "Verkstadsindustri", desc: "Allmän metallbearbetning" },
              ].map((b) => (
                <div key={b.name} className="bg-white border border-gray-200 rounded-lg p-5">
                  <div className="text-gray-900 font-semibold">{b.name}</div>
                  <div className="text-gray-500 text-sm mt-1">{b.desc}</div>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Kunskapsbank */}
      <RelatedArticles
        heading="Läs mer om våra branscher"
        subheading="Fördjupa dig i hur laserskärning och metallbearbetning används inom olika industrier."
        articles={[
          { title: "Laserskärning inom fordonsindustrin", slug: "laserskarning-fordonsindustrin", excerpt: "Hur precision och korta ledtider driver innovation inom fordonstillverkning." },
          { title: "Laserskärning för försvarsindustrin", slug: "laserskarning-forsvarsindustrin", excerpt: "Säkerhetskritiska komponenter med extrema krav på materialprestanda." },
          { title: "Laserskärning inom jordbrukssektorn", slug: "laserskarning-jordbruk", excerpt: "Slitstål och reparationsdelar för jordbruksmaskiner och lantbruksutrustning." },
          { title: "Laserskärning inom byggindustrin", slug: "laserskarning-byggindustrin", excerpt: "Konstruktionsstål, fasadelement och speciallösningar för byggprojekt." },
          { title: "Laserskärning inom energisektorn", slug: "laserskarning-energisektorn", excerpt: "Komponenter för vindkraft, solenergi och konventionell energiproduktion." },
          { title: "Välja rätt plåtleverantör", slug: "valja-underleverantor-plat", excerpt: "Checklista och tips för att hitta en pålitlig partner för laserskärning." },
        ]}
      />
    </Layout>
  );
}
